import React, { useState, useEffect } from "react";
import {
  Package,
  Eye,
  Download,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
} from "lucide-react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import LoadingSpinner from "../ProductList/LoadingSpinner";
import Snow from "../Snow/Snow";

const OrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const navigate = useNavigate();
  const ordersPerPage = 10;

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("Auth");
        if (!token) {
          throw new Error("Authorization token not found");
        }

        const response = await axios.get(
          "https://api.elza2.store/api/v1/order/user",
          {
            headers: { Authorization: token },
            params: {
              page: currentPage,
              limit: ordersPerPage,
            },
          }
        );

        setOrders(response.data.orders);
        setTotalPages(response.data.totalPages);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchOrders();
  }, [currentPage]);

  const handleRefresh = () => {
    setCurrentPage(1);
  };

  const renderOrderStatus = (phase) => {
    const statusColors = {
      pending: "bg-yellow-100 text-yellow-800",
      processing: "bg-blue-100 text-blue-800",
      shipped: "bg-green-100 text-green-800",
      delivered: "bg-purple-100 text-purple-800",
      cancelled: "bg-red-100 text-red-800",
    };

    return (
      <span
        className={`px-3 py-1 rounded-full text-xs font-medium ${
          statusColors[phase] || "bg-gray-100 text-gray-800"
        }`}
      >
        {phase}
      </span>
    );
  };

  if (loading)
    return <LoadingSpinner />;

  if (error)
    return (
      <div className="bg-red-50 p-4 rounded-lg text-center">
        <p className="text-red-600">{error}</p>
      </div>
    );

  return (
    <div className="bg-gray-50 min-h-screen p-8">
      {/* <Snow /> */}
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800 flex items-center">
            <Package className="mr-4 text-blue-500" />
            My Orders
          </h1>
          <button
            onClick={handleRefresh}
            className="flex items-center bg-white border border-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-100 transition"
          >
            <RefreshCw className="mr-2" size={16} /> Refresh
          </button>
        </div>

        {orders.length === 0 ? (
          <div className="bg-white shadow-md rounded-lg p-8 text-center">
            <Package className="mx-auto mb-4 text-gray-300" size={64} />
            <p className="text-gray-600">No orders found</p>
          </div>
        ) : (
          <div className="bg-white shadow-md rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    {["Order ID", "Date", "Total", "Status", "Actions"].map(
                      (header) => (
                        <th
                          key={header}
                          className="p-4 text-left text-xs text-gray-500 uppercase tracking-wider"
                        >
                          {header}
                        </th>
                      )
                    )}
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => (
                    <tr
                      key={order._id}
                      className="border-b hover:bg-gray-50 transition-colors"
                    >
                      <td className="p-4 text-sm text-gray-700">
                        #{order._id.slice(-8)}
                      </td>
                      <td className="p-4 text-sm text-gray-600">
                        {format(new Date(order.createdAt), "MMMM dd, yyyy")}
                      </td>
                      <td className="p-4 font-semibold text-gray-800">
                        EGP {order.totalAfterDiscount?.toLocaleString()}
                      </td>
                      <td className="p-4">{renderOrderStatus(order.phase)}</td>
                      <td className="p-4 flex space-x-2">
                        <button
                          onClick={() =>
                            navigate(`/order-details/${order._id}`)
                          }
                          className="text-blue-500 hover:bg-blue-50 p-2 rounded-full transition"
                        >
                          <Eye size={20} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            <div className="bg-gray-50 p-4 flex justify-between items-center">
              <div className="text-sm text-gray-600">
                Page {currentPage} of {totalPages}
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentPage === 1}
                  className="bg-white border rounded-lg p-2 disabled:opacity-50 hover:bg-gray-100 transition"
                >
                  <ChevronLeft size={20} />
                </button>
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                  className="bg-white border rounded-lg p-2 disabled:opacity-50 hover:bg-gray-100 transition"
                >
                  <ChevronRight size={20} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrdersPage;
